import React from 'react';
import styles from './JobList.module.css';
import * as utils from '../../../utils/utils';
import Sidebar from '../../Sidebar/Sidebar';

const SELECTED_JOB_STORAGE_KEY = 'linkedupSelectedJob';

export default class JobList extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			jobs: [],
			searchQuery: '',
			workPlaceFilter: 'all',
			employmentFilter: 'all',

			offset: 0,
			limit: 20,
			hasMore: false,
			dataSource: '',

			isLoading: true,
			isLoadingMore: false,
			errorText: '',
		};
	}

	componentDidMount() {
		if (!utils.isLoggedIn()) {
			window.location.href = '/signin';
			return;
		}

		this.loadJobs(true);
	}

	normalizeJob = (item) => {
		const toTextArray = (value) => Array.isArray(value)
			? value.filter((entry) => typeof entry === 'string' && entry.trim() !== '').map((entry) => entry.trim())
			: [];

		return {
			jobId: Number(item.jobId) || 0,
			title: item.title || 'Untitled Role',
			summary: item.summary || '',
			city: item.city || '',
			state: item.state || '',
			country: item.country || '',
			department: item.department || '',
			employmentType: item.employmentType || '',
			workLevel: item.workLevel || '',
			workPlaceType: item.workPlaceType || '',
			salaryMin: item.salaryMin,
			salaryMax: item.salaryMax,
			currency: item.currency || 'USD',
			matchPercent: item.matchPercent !== undefined && item.matchPercent !== null
				? Number(item.matchPercent)
				: null,
			skills: toTextArray(item.skills),
			tools: toTextArray(item.tools),
			domains: toTextArray(item.domains),
			softSkills: toTextArray(item.softSkills),
			certificates: toTextArray(item.certificates),
			applyUrl: item.applyUrl || '',
		};
	};

	loadJobs = (isFreshLoad = false) => {
		const {
			offset,
			jobs,
			dataSource,
		} = this.state;

		const nextOffset = isFreshLoad ? 0 : offset;
		const endpointQueue = dataSource === ''
			? ['/job/getRecommendedJobs', '/job/getJobs']
			: [dataSource];

		this.setState({
			isLoading: isFreshLoad,
			isLoadingMore: !isFreshLoad,
			errorText: '',
		});

		const tryEndpoint = async (index) => {
			if (index >= endpointQueue.length) {
				throw new Error('Could not load jobs right now.');
			}

			const endpoint = endpointQueue[index];

			try {
				const responseData = await utils.sendRequest(endpoint, {
					offset: nextOffset,
				});

				const list = Array.isArray(responseData.jobs)
					? responseData.jobs.map(this.normalizeJob)
					: [];

				if (endpoint === '/job/getRecommendedJobs' && list.length === 0 && dataSource === '' && nextOffset === 0) {
					return tryEndpoint(index + 1);
				}

				const resolvedLimit = Number(responseData.limit) || 20;
				const nextJobs = isFreshLoad ? list : [...jobs, ...list];

				this.setState({
					jobs: nextJobs,
					offset: nextOffset + list.length,
					limit: resolvedLimit,
					hasMore: list.length === resolvedLimit,
					dataSource: endpoint,
					isLoading: false,
					isLoadingMore: false,
				});
			}
			catch (error) {
				if (dataSource === '' && nextOffset === 0 && index < endpointQueue.length - 1) {
					return tryEndpoint(index + 1);
				}

				throw error;
			}
		};

		tryEndpoint(0)
			.catch((error) => {
				this.setState({
					isLoading: false,
					isLoadingMore: false,
					errorText: error && error.message ? error.message : 'Could not load jobs right now.',
				});
			});
	};

	onSearchChange = (event) => {
		this.setState({
			searchQuery: event.target.value,
		});
	};

	onWorkPlaceFilterChange = (event) => {
		this.setState({
			workPlaceFilter: event.target.value,
		});
	};

	onEmploymentFilterChange = (event) => {
		this.setState({
			employmentFilter: event.target.value,
		});
	};

	getFilteredJobs = () => {
		const {
			jobs,
			searchQuery,
			workPlaceFilter,
			employmentFilter,
		} = this.state;

		const normalizedSearch = searchQuery.trim().toLowerCase();

		return jobs.filter((job) => {
			const locationText = [job.city, job.state, job.country].filter(Boolean).join(' ');
			const searchableText = [
				job.title,
				job.summary,
				job.department,
				job.workLevel,
				job.workPlaceType,
				job.employmentType,
				locationText,
				job.skills.join(' '),
				job.tools.join(' '),
				job.domains.join(' '),
			].join(' ').toLowerCase();

			const matchesSearch = normalizedSearch === '' || searchableText.includes(normalizedSearch);
			const matchesWorkPlace = workPlaceFilter === 'all' || job.workPlaceType.toLowerCase() === workPlaceFilter;
			const matchesEmployment = employmentFilter === 'all' || job.employmentType.toLowerCase() === employmentFilter;

			return matchesSearch && matchesWorkPlace && matchesEmployment;
		});
	};

	getWorkPlaceOptions = () => {
		const { jobs } = this.state;
		const uniqueValues = Array.from(new Set(jobs.map((job) => job.workPlaceType).filter(Boolean)));
		return uniqueValues;
	};

	getEmploymentOptions = () => {
		const { jobs } = this.state;
		const uniqueValues = Array.from(new Set(jobs.map((job) => job.employmentType).filter(Boolean)));
		return uniqueValues;
	};

	formatCompensation = (job) => {
		const toNumber = (value) => {
			if (value === null || value === undefined || value === '') {
				return null;
			}

			const parsed = Number(value);
			return Number.isFinite(parsed) ? parsed : null;
		};

		const min = toNumber(job.salaryMin);
		const max = toNumber(job.salaryMax);

		if (min === null && max === null) {
			return 'Compensation not listed';
		}

		const formatter = new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency: job.currency || 'USD',
			maximumFractionDigits: 0,
		});

		if (min !== null && max !== null) {
			return `${formatter.format(min)} - ${formatter.format(max)}`;
		}

		if (min !== null) {
			return `From ${formatter.format(min)}`;
		}

		return `Up to ${formatter.format(max)}`;
	};

	formatLocation = (job) => {
		const values = [job.city, job.state, job.country].filter((value) => value && value.trim() !== '');
		if (values.length === 0) {
			return 'Location not listed';
		}

		return values.join(', ');
	};

	goToDetails = (job) => {
		window.localStorage.setItem(SELECTED_JOB_STORAGE_KEY, JSON.stringify(job));
		window.location.href = `/job-details?jobId=${job.jobId}`;
	};

	renderJobCard = (job) => {
		const tags = [...job.skills, ...job.tools, ...job.domains].slice(0, 6);

		return (
			<article
				key={`job-${job.jobId}`}
				className={styles.jobCard}
				onClick={() => this.goToDetails(job)}
			>
				<div className={styles.jobTopRow}>
					<div>
						<h3 className={styles.jobTitle}>{job.title}</h3>
						<p className={styles.jobMetaLine}>
							<span>{job.department || 'General'}</span>
							<span className={styles.dot}>•</span>
							<span>{job.workLevel || 'Level not listed'}</span>
						</p>
					</div>

					<div className={styles.pillRow}>
						{
							job.matchPercent !== null && Number.isFinite(job.matchPercent)
								? <span className={styles.fitPill}>{Math.round(job.matchPercent)}% fit</span>
								: null
						}
						{
							job.workPlaceType !== ''
								? <span className={styles.neutralPill}>{job.workPlaceType}</span>
								: null
						}
					</div>
				</div>

				<p className={styles.jobSummary}>{job.summary || 'No summary available for this role.'}</p>

				<div className={styles.jobBottomRow}>
					<span className={styles.bottomMeta}>{this.formatLocation(job)}</span>
					<span className={styles.dot}>•</span>
					<span className={styles.bottomMeta}>{job.employmentType || 'Employment type not listed'}</span>
					<span className={styles.dot}>•</span>
					<span className={styles.bottomMeta}>{this.formatCompensation(job)}</span>
				</div>

				{
					tags.length > 0 &&
					<div className={styles.tagRow}>
						{tags.map((tag, index) => (
							<span key={`job-${job.jobId}-tag-${tag}-${index}`} className={styles.tag}>{tag}</span>
						))}
					</div>
				}
			</article>
		);
	};

	render() {
		const {
			isLoading,
			isLoadingMore,
			errorText,
			searchQuery,
			workPlaceFilter,
			employmentFilter,
			hasMore,
			jobs,
			dataSource,
		} = this.state;

		const filteredJobs = this.getFilteredJobs();
		const workPlaceOptions = this.getWorkPlaceOptions();
		const employmentOptions = this.getEmploymentOptions();
		const sourceLabel = dataSource === '/job/getRecommendedJobs' ? 'Recommended For You' : 'Latest Openings';

		return (
			<div className={styles.pageContainer}>
				<Sidebar />

				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					<section className={styles.headerPanel}>
						<div className={styles.brandBadge}>HQVibe</div>
						<h1 className={styles.title}>Discover roles aligned with your momentum.</h1>
						<p className={styles.subtitle}>
							Browse opportunities by fit, workplace style, and core stack. Open any role to review full details before applying.
						</p>
						<div className={styles.captionRow}>
							<span className={styles.caption}>{sourceLabel}</span>
							<span className={styles.caption}>{jobs.length} loaded</span>
						</div>
					</section>

					<section className={styles.mainCardPanel}>
						<div className={styles.controlRow}>
							<input
								className={styles.searchInput}
								placeholder='Search title, skills, department, or location'
								value={searchQuery}
								onChange={this.onSearchChange}
							/>

							<select
								className={styles.selectInput}
								value={workPlaceFilter}
								onChange={this.onWorkPlaceFilterChange}
							>
								<option value='all'>All workplace types</option>
								{workPlaceOptions.map((value) => (
									<option key={`workplace-${value}`} value={value.toLowerCase()}>{value}</option>
								))}
							</select>

							<select
								className={styles.selectInput}
								value={employmentFilter}
								onChange={this.onEmploymentFilterChange}
							>
								<option value='all'>All employment types</option>
								{employmentOptions.map((value) => (
									<option key={`employment-${value}`} value={value.toLowerCase()}>{value}</option>
								))}
							</select>
						</div>

						{
							isLoading
								? <div className={styles.stateBox}>Loading jobs...</div>
								: null
						}

						{
							!isLoading && errorText !== ''
								? <div className={styles.errorBox}>{errorText}</div>
								: null
						}

						{
							!isLoading && errorText === '' && filteredJobs.length === 0
								? <div className={styles.stateBox}>No jobs match your current filters.</div>
								: null
						}

						<div className={styles.listWrap}>
							{filteredJobs.map(this.renderJobCard)}
						</div>

						{
							hasMore && !isLoading &&
							<button
								type='button'
								className={styles.loadMoreButton}
								onClick={() => this.loadJobs(false)}
								disabled={isLoadingMore}
							>
								{isLoadingMore ? 'Loading more...' : 'Load More Roles'}
							</button>
						}
					</section>
				</div>
			</div>
		);
	}
}
